// Only this module writes the session status. Signed out is `null` data, since
// removing the query strands every observer on the old entry.
import { queryOptions, useQuery, type QueryKey } from "@tanstack/vue-query";
import { getActivePinia } from "pinia";
import { computed, reactive, readonly, ref, toValue, type MaybeRefOrGetter } from "vue";

import { adminLogin, adminLogout, getAdminStatus } from "../api/adminAuth";
import { login as postLogin, logout as postLogout } from "../api/auth";
import { getGrantStates, getUserStatus, type GrantState, type User } from "../api/portal";
import { maybeShowBatteryOptimizationPrompt } from "../capacitor/battery-optimization";
import { clearSessionToken } from "../capacitor/session-token";
import { useAdminBannersStore } from "../pinia/adminBanners";
import { useAdminReturnStore } from "../pinia/adminReturn";
import { useThemeStore } from "../pinia/theme";
import { useUserPortalStore } from "../pinia/userPortal";
import { isApiError, isHandled } from "../services/apiError";
import { appQueryClient } from "./client";

export const sessionKeys = {
  admin: ["session", "admin"] as const,
  user: ["session", "user"] as const,
};

// Untyped beyond the identity: around fifty readers each take their own fields.
export type AdminStatus = Record<string, any> & { AdminID?: string };
export type { User };

export const SESSION_FRESH_MS = 3 * 60 * 1000;
export const ADMIN_STATUS_POLL_MS = 30_000;

const isSessionKey = (queryKey: QueryKey) => queryKey[0] === sessionKeys.admin[0];

// A dead session stops the poll; the response policy is already redirecting.
function sessionEnded(error: unknown): boolean {
  return isHandled(error) || (isApiError(error) && error.status === 401);
}

// Keeps the session entries, which observers hold on to.
function dropSessionData(): void {
  const client = appQueryClient();
  const others = { predicate: (query: { queryKey: QueryKey }) => !isSessionKey(query.queryKey) };
  void client.cancelQueries(others);
  client.removeQueries(others);
}

//region Admin

// Set in the same tick as the status, so pinia/prefs.ts switches admins before
// anything else writes. The cache notifies a tick later.
const adminId = ref<string | null>(null);
export const signedInAdminId = readonly(adminId);

function noteAdmin(status: AdminStatus | null): AdminStatus | null {
  const id = status?.AdminID;
  adminId.value = typeof id === "string" && id !== "" ? id : null;
  return status;
}

// While 2FA is unverified the status holds only the 2FA fields, so AdminID is
// kept from before. Sign-in and sign-out clear it first.
function keepIdentity(previous: AdminStatus | null | undefined, next: AdminStatus | null): AdminStatus | null {
  if (next && !next.AdminID && previous?.AdminID) return { ...previous, ...next };
  return next;
}

function adminStatusOptions() {
  return queryOptions({
    queryKey: sessionKeys.admin,
    queryFn: async (): Promise<AdminStatus | null> => {
      const previous = appQueryClient().getQueryData<AdminStatus | null>(sessionKeys.admin);
      const next = keepIdentity(previous, (await getAdminStatus()) ?? null);
      return noteAdmin(next);
    },
    staleTime: SESSION_FRESH_MS,
    // The poll asks again on its own cadence, so a retry only delays the redirect.
    retry: false,
  });
}

/**
 * From the cache while fresh, else from the server. A failure signs out and
 * rethrows, except a transient one, which keeps the status the tab last read.
 * `maxAgeMs` lets a page reuse the status the route guard has fetched.
 */
export async function fetchAdminStatus(
  options: { force?: boolean; maxAgeMs?: number } = {},
): Promise<AdminStatus | null> {
  try {
    return await appQueryClient().fetchQuery({
      ...adminStatusOptions(),
      staleTime: options.force ? 0 : (options.maxAgeMs ?? SESSION_FRESH_MS),
    });
  } catch (error) {
    if (!isTransientSessionFailure(error)) forgetAdmin();
    throw error;
  }
}

function forgetAdmin(): void {
  appQueryClient().setQueryData(sessionKeys.admin, noteAdmin(null));
  if (!getActivePinia()) return;
  useAdminBannersStore().$reset();
  useAdminReturnStore().$reset();
}

export function cachedAdminStatus(): AdminStatus | null | undefined {
  return appQueryClient().getQueryData<AdminStatus | null>(sessionKeys.admin);
}

/** Ends the session locally. `clearCache: false` keeps a public page's own reads. */
export function endAdminSession(options: { clearCache?: boolean } = {}): void {
  if (options.clearCache !== false) dropSessionData();
  forgetAdmin();
}

// A knoc draft belongs to the admin who typed it. Imported lazily to keep the
// wizard out of the user portal.
async function forgetKnocDraft(): Promise<void> {
  if (!getActivePinia()) return;
  const { useWizardStore } = await import("../pinia/wizard");
  useWizardStore().resetAndClearPersisted();
}

// A failed sign-in keeps the login page's own reads, which it is still showing.
export async function signInAdmin(credentials: { UserName?: string; Password?: string }): Promise<void> {
  try {
    await adminLogin(credentials);
  } catch (error) {
    forgetAdmin();
    throw error;
  }
  await forgetKnocDraft();
  endAdminSession();
}

export async function signOutAdmin(): Promise<void> {
  try {
    await adminLogout();
  } finally {
    await forgetKnocDraft();
    endAdminSession();
  }
}

// AdminApp polls with it while an admin page is open.
export function useAdminStatusQuery(enabled: MaybeRefOrGetter<boolean>) {
  return useQuery({
    ...adminStatusOptions(),
    enabled: computed(() => toValue(enabled)),
    staleTime: ADMIN_STATUS_POLL_MS,
    refetchInterval: (query) => (sessionEnded(query.state.error) ? false : ADMIN_STATUS_POLL_MS),
  });
}

/**
 * Never fetches. `user` reads the cache since the observer lags a tick behind
 * `await fetchStatus()`.
 */
export function useAdminSession() {
  const query = useQuery({ ...adminStatusOptions(), enabled: false });
  const client = appQueryClient();
  return reactive({
    get user(): AdminStatus | null {
      void query.data.value;
      return client.getQueryData<AdminStatus | null>(sessionKeys.admin) ?? null;
    },
    fetchStatus: fetchAdminStatus,
    login: signInAdmin,
    logout: signOutAdmin,
  });
}

//endregion

//region User portal

function userPortalStore() {
  return getActivePinia() ? useUserPortalStore() : null;
}

function rememberUser(status: User | null): User | null {
  const store = userPortalStore();
  if (store) {
    store.lastStatus = status;
    store.lastStatusAt = status ? Date.now() : null;
  }
  return status;
}

function userStatusOptions() {
  return queryOptions({
    queryKey: sessionKeys.user,
    queryFn: async (): Promise<User | null> => rememberUser(await getUserStatus()),
    staleTime: SESSION_FRESH_MS,
    retry: false,
  });
}

// A dropped connection, a timeout, a server error or the account's request
// budget running out (429) says nothing about the session. The next poll asks
// again.
export function isTransientSessionFailure(error: unknown): boolean {
  if (!isApiError(error) || isHandled(error)) return false;
  if (error.kind === "network" || error.kind === "timeout" || error.kind === "aborted") return true;
  return error.kind === "http" && (error.status >= 500 || error.status === 429);
}

/**
 * From the cache while fresh, else from the server. A failure signs out and
 * rethrows, except a transient one, which keeps the status the tab last read.
 * `maxAgeMs` lets a page reuse the status the route guard has fetched.
 */
export async function fetchUserStatus(options: { force?: boolean; maxAgeMs?: number } = {}): Promise<User | null> {
  try {
    return await appQueryClient().fetchQuery({
      ...userStatusOptions(),
      staleTime: options.force ? 0 : (options.maxAgeMs ?? SESSION_FRESH_MS),
    });
  } catch (error) {
    if (!isTransientSessionFailure(error)) forgetUser();
    throw error;
  }
}

// Uncached: it only tells the dashboard when to read the whole status again.
export function fetchGrantStates(ids: readonly string[]): Promise<GrantState[]> {
  return getGrantStates(ids);
}

function forgetUser(): void {
  appQueryClient().setQueryData(sessionKeys.user, null);
  userPortalStore()?.$reset();
}

export function cachedUserStatus(): User | null | undefined {
  return appQueryClient().getQueryData<User | null>(sessionKeys.user);
}

/** Seeds the cache from the tab's last status so a reload paints at once. */
export function restoreUserStatus(): void {
  const store = userPortalStore();
  if (!store?.lastStatus || !store.lastStatusAt) return;
  appQueryClient().setQueryData(sessionKeys.user, store.lastStatus, { updatedAt: store.lastStatusAt });
}

export function endUserSession(options: { clearCache?: boolean } = {}): void {
  if (options.clearCache !== false) dropSessionData();
  forgetUser();
}

/**
 * The native app now talks to another server, so nothing the old one answered
 * applies: its login buttons, its theme, its session. The cached reads are reset
 * rather than removed, because the shell observes the theme and a removed query
 * would leave it on the old server's accent; a reset keeps the observers and
 * refetches what is on screen from the new server. The saved theme goes first,
 * since it seeds the reset. The old server's bearer is cleared, or the next
 * request would hand it to the new one.
 */
export async function forgetServer(): Promise<void> {
  const client = appQueryClient();
  const others = { predicate: (query: { queryKey: QueryKey }) => !isSessionKey(query.queryKey) };
  await client.cancelQueries(others);
  await clearSessionToken();
  if (getActivePinia()) useThemeStore().$reset();
  forgetUser();
  await client.resetQueries(others);
}

// After a 409 sessionSlotRequired, so the guard keeps the user on the session limit screen.
export function markSessionSlotUnavailable(): void {
  appQueryClient().setQueryData<User | null>(sessionKeys.user, (status) =>
    status ? rememberUser({ ...status, HasSessionSlot: false }) : status,
  );
}

// After a 401 passwordChangeRequired, so the guard keeps the user on the change-password screen.
export function markPasswordChangeRequired(): void {
  appQueryClient().setQueryData<User | null>(sessionKeys.user, (status) =>
    status ? rememberUser({ ...status, PasswordChangeRequired: true }) : status,
  );
}

// Returns the response body, which on native is the session token.
export async function signInUser(credentials: string): Promise<unknown> {
  let answer: unknown;
  try {
    answer = await postLogin(credentials);
  } catch (error) {
    forgetUser();
    throw error;
  }
  endUserSession();
  maybeShowBatteryOptimizationPrompt().catch(() => {});
  return answer;
}

export async function signOutUser(): Promise<void> {
  try {
    await postLogout();
  } finally {
    endUserSession();
  }
}

// Only listens for invalidations; useStatusPolling owns the cadence. Mounted
// once, by App.vue.
export function useUserStatusQuery(enabled: MaybeRefOrGetter<boolean>) {
  return useQuery({ ...userStatusOptions(), enabled: computed(() => toValue(enabled)) });
}

export function useUserSession() {
  const query = useQuery({ ...userStatusOptions(), enabled: false });
  const client = appQueryClient();
  return reactive({
    get user(): User | null {
      void query.data.value;
      return client.getQueryData<User | null>(sessionKeys.user) ?? null;
    },
    fetchStatus: fetchUserStatus,
    login: signInUser,
    logout: signOutUser,
  });
}

//endregion
